const DNN_MOUNT_SELECTOR = '#escape-room-root';
const DNN_DEFAULT_SERVICE_ROOT = '/DesktopModules/EscapeRoom/API/';

function parseJsonAttribute(rawValue, attributeName) {
  if (!rawValue) return {};

  try {
    const parsed = JSON.parse(rawValue);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (error) {
    console.warn(`[EscapeRoom] Failed to parse ${attributeName}:`, error);
    return {};
  }
}

function pickFirst(...values) {
  return values.find((value) => value !== undefined && value !== null);
}

function parseBoolean(value) {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value !== 'string') return false;
  const normalized = value.trim().toLowerCase();
  return normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'y';
}

function mapModuleConfig(moduleConfig) {
  return {
    moduleId: pickFirst(moduleConfig.moduleId, moduleConfig.ModuleId),
    tabId: pickFirst(moduleConfig.tabId, moduleConfig.TabId),
    tabModuleId: pickFirst(moduleConfig.tabModuleId, moduleConfig.TabModuleId),
    portalId: pickFirst(moduleConfig.portalId, moduleConfig.PortalId)
  };
}

function mapSecurityConfig(securityConfig) {
  const currentUser = pickFirst(
    securityConfig.currentUser,
    securityConfig.CurrentUser,
    securityConfig.user,
    securityConfig.User,
    pickFirst(securityConfig.userId, securityConfig.UserId) !== undefined
      ? {
          id: pickFirst(securityConfig.userId, securityConfig.UserId),
          username: pickFirst(securityConfig.username, securityConfig.Username, null)
        }
      : null
  );

  return {
    currentUser,
    permissions: {
      canView: parseBoolean(pickFirst(securityConfig.canView, securityConfig.CanView, false)),
      canEdit: parseBoolean(pickFirst(securityConfig.canEdit, securityConfig.CanEdit, false)),
      isAdmin: parseBoolean(pickFirst(securityConfig.isAdmin, securityConfig.IsAdmin, false))
    }
  };
}

function mapRuntimeConfig(runtimeConfig) {
  return {
    apiBaseUrl: pickFirst(runtimeConfig.apiBaseUrl, runtimeConfig.apiUrl, runtimeConfig.ApiBaseUrl, runtimeConfig.ApiUrl),
    serviceRoot: pickFirst(runtimeConfig.serviceRoot, runtimeConfig.ServiceRoot),
    antiForgeryToken: pickFirst(runtimeConfig.antiForgeryToken, runtimeConfig.requestVerificationToken, runtimeConfig.AntiForgeryToken, runtimeConfig.RequestVerificationToken),
    currentUser: pickFirst(runtimeConfig.currentUser, runtimeConfig.CurrentUser),
    permissions: pickFirst(runtimeConfig.permissions, runtimeConfig.Permissions)
  };
}

function ensureTrailingSlash(value = '') {
  return value.endsWith('/') ? value : `${value}/`;
}

function readDnnVar(name) {
  if (typeof window === 'undefined') return null;
  const dnn = window.dnn;
  const getter = dnn?.getVar;
  if (typeof getter !== 'function') return null;
  const value = getter.call(dnn, name);
  return value !== undefined && value !== null && value !== '' ? value : null;
}

function derivePermissionsFromDnnVars() {
  const canEdit = parseBoolean(
    readDnnVar('sf_canEdit') ||
    readDnnVar('sf_canedit') ||
    readDnnVar('sf_editable') ||
    readDnnVar('sf_isEditable') ||
    readDnnVar('sf_iseditable') ||
    readDnnVar('sf_isEditMode') ||
    readDnnVar('sf_iseditmode') ||
    readDnnVar('canEdit') ||
    readDnnVar('CanEdit') ||
    readDnnVar('editable') ||
    readDnnVar('isEditable') ||
    readDnnVar('IsEditable') ||
    readDnnVar('isEditMode') ||
    readDnnVar('IsEditMode')
  );

  const isAdmin = parseBoolean(
    readDnnVar('sf_isSuperUser') ||
    readDnnVar('sf_issuperuser') ||
    readDnnVar('isSuperUser') ||
    readDnnVar('IsSuperUser') ||
    readDnnVar('sf_isAdmin') ||
    readDnnVar('sf_isadmin') ||
    readDnnVar('isAdmin') ||
    readDnnVar('IsAdmin')
  );

  if (!canEdit && !isAdmin) return null;

  return {
    canView: true,
    canEdit,
    isAdmin
  };
}

function derivePermissionsFromDnnPageState() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return null;

  const dotNetNuke = window.DotNetNuke || window.dotNetNuke;
  const canEdit = Boolean(
    dotNetNuke?.EditMode ||
    dotNetNuke?.editMode ||
    document.body?.classList?.contains('dnnEditState') ||
    document.body?.classList?.contains('dnnEditMode') ||
    document.querySelector('.dnnEditState, .dnnEditMode, #ControlBar, #dnnControlPanel, #dnnCPWrap, #dnn_EditBar, .dnnEditBar, .dnnActionMenu, .dnnModuleActions')
  );

  const isAdmin = Boolean(
    document.body?.classList?.contains('dnnAdmin') ||
    document.querySelector('#personaBar-iframe, .personaBarContainer, .dnnAdminTabNav')
  );

  if (!canEdit && !isAdmin) return null;

  return {
    canView: true,
    canEdit,
    isAdmin
  };
}

function mergePermissions(...sources) {
  const merged = {
    canView: false,
    canEdit: false,
    isAdmin: false
  };

  let hasAny = false;

  for (const source of sources) {
    if (!source || typeof source !== 'object') continue;
    hasAny = true;
    merged.canView = merged.canView || parseBoolean(source.canView);
    merged.canEdit = merged.canEdit || parseBoolean(source.canEdit);
    merged.isAdmin = merged.isAdmin || parseBoolean(source.isAdmin);
  }

  return hasAny ? merged : null;
}

function deriveModuleIdFromDom(rootElement) {
  if (!rootElement || typeof rootElement.closest !== 'function') return null;
  const container = rootElement.closest('[id*="dnn_ctr"]');
  if (!container?.id) return null;
  const match = container.id.match(/dnn_ctr(\d+)_/i);
  return match?.[1] || null;
}

function deriveTabIdFromDnn() {
  return (
    readDnnVar('sf_tabId') ||
    readDnnVar('sf_tabid') ||
    readDnnVar('tabid') ||
    null
  );
}

function deriveServiceRootFromApiBaseUrl(apiBaseUrl) {
  if (!apiBaseUrl) return null;
  const normalized = String(apiBaseUrl).trim();
  if (!normalized) return null;
  if (!/\/DesktopModules\/.+\/API\/?/i.test(normalized)) return null;
  return ensureTrailingSlash(normalized.replace(/\/api$/i, '/API'));
}

function deriveServiceRootFromScriptTag() {
  if (typeof document === 'undefined') return null;

  const script = Array.from(document.querySelectorAll('script[src]')).find((tag) =>
    /\/DesktopModules\/[^/]+\/scripts\/escape-room\.js(\?|$)/i.test(tag.getAttribute('src') || '')
  );

  if (!script) return null;

  const src = script.getAttribute('src') || '';
  const match = src.match(/(\/DesktopModules\/[^/]+)\/scripts\/escape-room\.js(?:\?|$)/i);
  if (!match) return null;

  return ensureTrailingSlash(`${match[1]}/API/`);
}

function resolveDnnServiceRoot(runtimeConfig) {
  return (
    runtimeConfig.serviceRoot ||
    deriveServiceRootFromApiBaseUrl(runtimeConfig.apiBaseUrl) ||
    deriveServiceRootFromScriptTag() ||
    DNN_DEFAULT_SERVICE_ROOT
  );
}

export function resolveHostContext({ fallbackSelector = '#root' } = {}) {
  const dnnRootElement = document.querySelector(DNN_MOUNT_SELECTOR);

  if (!dnnRootElement) {
    return {
      mountElement: document.querySelector(fallbackSelector),
      runtimeConfig: {}
    };
  }

  const moduleConfig = parseJsonAttribute(dnnRootElement.getAttribute('data-module'), 'data-module');
  const securityConfig = parseJsonAttribute(dnnRootElement.getAttribute('data-security'), 'data-security');
  const runtimeConfig = mapRuntimeConfig(parseJsonAttribute(dnnRootElement.getAttribute('data-runtime'), 'data-runtime'));
  const securityContext = mapSecurityConfig(securityConfig);
  const dnnVarPermissions = derivePermissionsFromDnnVars();
  const dnnPageStatePermissions = derivePermissionsFromDnnPageState();
  const moduleContext = mapModuleConfig(moduleConfig);
  const resolvedModuleId = moduleContext.moduleId ?? deriveModuleIdFromDom(dnnRootElement);
  const resolvedTabId = moduleContext.tabId ?? deriveTabIdFromDnn();
  const serviceRoot = resolveDnnServiceRoot(runtimeConfig);

  return {
    mountElement: dnnRootElement,
    runtimeConfig: {
      ...moduleContext,
      moduleId: resolvedModuleId,
      tabId: resolvedTabId,
      ...runtimeConfig,
      currentUser: runtimeConfig.currentUser ?? securityContext.currentUser,
      permissions: mergePermissions(runtimeConfig.permissions, securityContext.permissions, dnnVarPermissions, dnnPageStatePermissions),
      serviceRoot
    }
  };
}

export { DNN_MOUNT_SELECTOR };
